import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { LogOut, Shield, Edit3, Ticket, Trophy, Target, Globe, BookOpen, ChevronRight } from 'lucide-react';
import { Role } from "@/gql/graphql.ts";
import { useAuth } from '@/context/AuthContext';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { UserAvatar } from '@/components/ui/UserAvatar';
// import { generateStravaState } from '@/lib/strava-state';
import { MY_BETS } from '@/graphql/queries';
import { cn } from '@/lib/utils';

const LANGUAGES = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
];

export function Profile() {
    const { user, logout } = useAuth();
    const { t, i18n } = useTranslation('profile');

    // const { data: stravaData } = useQuery(GET_STRAVA_AUTH_URL, {
    //     skip: user?.isStravaConnected ?? false,
    // });

    const { data: betsData } = useQuery(MY_BETS, {
        variables: { pagination: { page: 1, limit: 100 } },
    });

    const stats = useMemo(() => {
        const bets = betsData?.myBets?.items ?? [];
        const uniqueRaces = new Set(bets.map(b => b.race?.id).filter(Boolean));
        const totalScore = bets.reduce((sum, b) => sum + (b.score ?? 0), 0);
        return {
            bets: betsData?.myBets?.totalCount ?? 0,
            races: uniqueRaces.size,
            score: totalScore,
        };
    }, [betsData]);

    const handleLogout = async () => {
        await logout();
    };

    // const handleLinkStrava = () => {
    //     if (stravaData?.stravaAuthUrl) {
    //         const state = generateStravaState();
    //         window.location.href = `${stravaData.stravaAuthUrl}&state=${encodeURIComponent(state)}`;
    //     }
    // };

    return (
        <Page>
            <PageHeader
                title={t('title')}
                backLink="/"
                width="narrow"
            />

            <PageContent width="narrow" className="space-y-6 pb-24 md:pb-6">

                {/* --- HERO + STATS --- */}
                <div>
                    {/* Hero gradient card */}
                    <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-600 to-emerald-700 rounded-3xl px-6 pt-8 pb-10 overflow-hidden">
                        {/* Decorative circles */}
                        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10" />

                        <div className="relative flex flex-col items-center">
                            <div className="relative mb-3">
                                <UserAvatar
                                    src={user?.pictureUrl}
                                    alt={user?.username}
                                    size="lg"
                                    className="border-4 border-white/90 shadow-xl"
                                />
                                {user?.roles?.includes(Role.Admin) && (
                                    <div className="absolute -bottom-1 -right-1 bg-amber-400 text-white p-1.5 rounded-full border-2 border-white" title="Admin">
                                        <Shield size={12} fill="currentColor" />
                                    </div>
                                )}
                            </div>

                            <h1 className="text-xl font-bold text-white">{user?.username}</h1>
                            <p className="text-emerald-200 text-sm">{user?.email}</p>

                            <Link
                                to="/profile/edit"
                                className="mt-4 px-4 py-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white rounded-xl text-sm font-bold flex items-center gap-2 active:scale-95 transition-all"
                            >
                                <Edit3 size={16} />
                                {t('editProfile')}
                            </Link>
                        </div>
                    </div>

                    {/* Stats row - overlaps the hero */}
                    <div className="grid grid-cols-3 gap-3 -mt-5 relative z-10 px-3">
                        <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center">
                            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center mb-1.5">
                                <Ticket size={16} className="text-emerald-600" />
                            </div>
                            <div className="text-lg font-black text-slate-900">{stats.bets}</div>
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('stats.bets')}</div>
                        </div>
                        <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center">
                            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mb-1.5">
                                <Trophy size={16} className="text-amber-600" />
                            </div>
                            <div className="text-lg font-black text-slate-900">{stats.races}</div>
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('stats.races')}</div>
                        </div>
                        <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mb-1.5">
                                <Target size={16} className="text-blue-600" />
                            </div>
                            <div className="text-lg font-black text-slate-900">{stats.score % 1 === 0 ? stats.score : stats.score.toFixed(1)}</div>
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{t('stats.score')}</div>
                        </div>
                    </div>
                </div>

                {/* --- SETTINGS --- */}
                <div className="space-y-3">
                    <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest pl-1">{t('settings.title')}</h2>

                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {/* Language */}
                        <div className="flex items-center gap-3 px-4 py-3.5">
                            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                <Globe size={18} className="text-emerald-600" />
                            </div>
                            <span className="text-sm font-semibold text-slate-700 flex-1">{t('settings.language')}</span>
                            <div className="flex gap-1">
                                {LANGUAGES.map(lang => (
                                    <button
                                        key={lang.code}
                                        onClick={() => i18n.changeLanguage(lang.code)}
                                        className={cn(
                                            'px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95',
                                            i18n.language.startsWith(lang.code)
                                                ? 'bg-emerald-600 text-white shadow-sm'
                                                : 'bg-slate-100 text-slate-500'
                                        )}
                                    >
                                        {lang.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Strava */}
                        { /* <div className="flex items-center gap-3 px-4 py-3.5">
                            <div className={cn(
                                'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
                                user?.isStravaConnected ? 'bg-[#FC4C02]/10' : 'bg-slate-100'
                            )}>
                                <StravaLogo className={user?.isStravaConnected ? 'text-[#FC4C02]' : 'text-slate-400'} />
                            </div>
                            <span className="text-sm font-semibold text-slate-700 flex-1">Strava</span>
                            {user?.isStravaConnected ? (
                                <div className="flex items-center gap-1.5 text-emerald-600">
                                    <Check size={16} />
                                    <span className="text-xs font-bold">{t('strava.connected')}</span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleLinkStrava}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#FC4C02] text-white active:scale-95 transition-all"
                                >
                                    {t('strava.linkAccount')}
                                </button>
                            )}
                        </div> */ }

                        {/* Scoring */}
                        <Link to="/scoring" className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors">
                            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                <BookOpen size={18} className="text-indigo-600" />
                            </div>
                            <span className="text-sm font-semibold text-slate-700 flex-1">{t('settings.scoring')}</span>
                            <ChevronRight size={16} className="text-slate-300" />
                        </Link>
                    </div>
                </div>

                {/* --- LOGOUT --- */}
                <button
                    onClick={handleLogout}
                    className="w-full bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-3.5 flex items-center gap-3 active:scale-95 transition-all"
                >
                    <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                        <LogOut size={18} className="text-red-500" />
                    </div>
                    <span className="text-sm font-semibold text-red-500">{t('logout')}</span>
                </button>

                <p className="text-center text-xs text-slate-300">
                    {t('common:footer.version')}
                </p>

            </PageContent>
        </Page>
    );
}

// function StravaLogo({ className }: { className?: string }) {
//     return (
//         <svg viewBox="0 0 24 24" fill="currentColor" className={cn('w-5 h-5', className)}>
//             <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
//         </svg>
//     );
// }
